"use client";

import { useSyncExternalStore } from "react";

/**
 * Is this page being read at the DESKTOP layout, 1024px and up?
 *
 * One number for the whole route. The page grid, the booking rail, the pinned
 * price bar, the bottom tab bar and every field overlay all change at `lg`, and
 * while three of them said `lg` and two said `xl` the band between 1024 and
 * 1280 showed a sticky rail AND a fixed bar — two CTAs for one action. It lives
 * here rather than in any one of those files so there is no "which file owns
 * the breakpoint" to get wrong next time.
 */
const DESKTOP_QUERY = "(min-width: 64rem)";

/** MODULE SCOPE, not an inline closure. `useSyncExternalStore` tears down and
 *  re-runs `subscribe` whenever the function identity changes, so an inline one
 *  removed and re-added a `matchMedia` listener on EVERY render of every caller
 *  — twice per booking rail, on each `+`/`−` tap and each month change, which
 *  is the page's INP-critical path. A stable reference subscribes once. */
function subscribe(onChange: () => void): () => void {
  const list = window.matchMedia(DESKTOP_QUERY);
  list.addEventListener("change", onChange);
  return () => list.removeEventListener("change", onChange);
}

function getSnapshot(): boolean {
  return window.matchMedia(DESKTOP_QUERY).matches;
}

/**
 * SSR answers `false` — the phone branch — and that is deliberate for the field
 * overlays: the overlay is closed on first paint either way, so nothing about
 * the initial HTML differs, and a wrong guess would otherwise mean a desktop
 * visitor's first click opened a sheet.
 *
 * Callers that render DIFFERENT markup per branch (the reviews list) therefore
 * ship the phone shape and swap on hydration. That is the right trade for a
 * below-the-fold section and the wrong one above it — do not reach for this
 * hook to choose hero markup.
 */
function getServerSnapshot(): boolean {
  return false;
}

export function useIsDesktop(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
